import React, { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { AppButton, Callout } from '../../ui';
import { usePlan } from '../../../contexts/PlanContext';
import NewForYouModal from '../../Notifications/NewForYouModal';
import useStudentHome from './useStudentHome';
import {
  assignmentsFor, homeModules, statTiles, upcomingFor,
} from './studentHomeRules';
import { WelcomeBanner, WelcomeRow } from './cards/Welcome';
import StatTiles from './cards/StatTiles';
import LiveNow from './cards/LiveNow';
import FeesDue from './cards/FeesDue';
import ContinueLearning from './cards/ContinueLearning';
import ProfileCard from './cards/ProfileCard';
import LevelCard from './cards/LevelCard';
import StreakCard from './cards/StreakCard';
import MyCoursesCard from './cards/MyCoursesCard';
import UpcomingCard from './cards/UpcomingCard';
import PerformanceCard from './cards/PerformanceCard';
import AssignmentsCard from './cards/AssignmentsCard';
import BadgesCard from './cards/BadgesCard';
import HelpCard from './cards/HelpCard';
import AiInsightCard from './cards/AiInsightCard';
import AiReportCard from './cards/AiReportCard';
import RecommendedRail from './cards/RecommendedRail';
import HomeSkeleton from './cards/HomeSkeleton';

const Retry = ({ onClick }) => (
  <AppButton variant="outline" size="xs" onClick={onClick}>
    <RefreshCw size={14} />
    Try again
  </AppButton>
);

/**
 * /s/dashboard. The DOM order is the reading order at every width; nothing is
 * rearranged with `order`, so a screen reader and a phone meet the same page.
 *
 * `tw-cq-page` measures the room the shell actually leaves, because the sidebar
 * makes that smaller on a tablet than on a large phone. `page-*:` and `card-*:` read
 * it. The rail is two grid rows beside one spanning column: that is what puts the
 * profile above the main column's top on a laptop and the AI cards below its end on
 * a phone, from the same order.
 */
const StudentHomePage = () => {
  const { hasPlanFeature, isFeatureHidden, loading: planLoading } = usePlan();

  const modules = useMemo(
    () => homeModules((feature) => !isFeatureHidden(feature) && hasPlanFeature(feature)),
    [hasPlanFeature, isFeatureHidden],
  );

  const {
    data, status, stale, retry,
  } = useStudentHome({ enabled: !planLoading, attendance: modules.attendance });

  return (
    <div className="tw-cq-page">
      <NewForYouModal />
      <div className="tw-mx-auto tw-flex tw-max-w-[1760px] tw-flex-col tw-gap-3 tw-text-content page-sm:tw-gap-4">
        {status === 'loading' && <HomeSkeleton />}

        {status === 'failed' && (
          <Callout tone="danger" role="alert" action={<Retry onClick={retry} />}>
            We could not load your dashboard. Check your connection and try again.
          </Callout>
        )}

        {status === 'ready' && data && (
          <>
            {stale && (
              <Callout tone="warning" action={<Retry onClick={retry} />}>
                This may be out of date. We could not refresh your dashboard.
              </Callout>
            )}

            <div className="tw-grid tw-grid-cols-1 tw-gap-3 page-sm:tw-gap-4 page-lg:tw-grid-cols-[264px_minmax(0,1fr)] page-lg:tw-grid-rows-[auto_1fr] page-xl:tw-grid-cols-[296px_minmax(0,1fr)]">
              <ProfileCard profile={data.profile} />

              <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 page-sm:tw-gap-4 page-lg:tw-col-start-2 page-lg:tw-row-span-2 page-lg:tw-row-start-1">
                <WelcomeBanner profile={data.profile} streak={data.streak} />
                <WelcomeRow profile={data.profile} streak={data.streak} />
                <LiveNow courses={data.courses} />
                <StatTiles tiles={statTiles(data.metrics, modules)} />
                {modules.fees && <FeesDue onPaid={retry} />}
                <ContinueLearning />

                <div className="tw-grid tw-grid-cols-2 tw-gap-3 page-sm:tw-gap-4">
                  <LevelCard level={data.level} />
                  <StreakCard streak={data.streak} />
                </div>

                <div className="tw-grid tw-grid-cols-1 tw-gap-3 page-sm:tw-gap-4 page-md:tw-grid-cols-2 page-xl:tw-grid-cols-6">
                  <MyCoursesCard courses={data.courses} className="page-xl:tw-col-span-3" />
                  <UpcomingCard items={upcomingFor(data.upcoming, modules)} className="page-xl:tw-col-span-3" />
                  <PerformanceCard performance={data.performance} className="page-md:tw-col-span-2 page-xl:tw-col-span-4" />
                  <AssignmentsCard assignments={assignmentsFor(data.assignments, modules)} className="page-xl:tw-col-span-2" />
                  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 page-sm:tw-gap-4 page-xl:tw-col-span-6 page-xl:tw-grid page-xl:tw-grid-cols-2">
                    <BadgesCard badges={data.badges} showCertificates={modules.selfPaced} className="tw-flex-1" />
                    <HelpCard />
                  </div>
                </div>
              </div>

              {modules.ai && (
                <div className="tw-grid tw-min-w-0 tw-grid-cols-1 tw-content-start tw-gap-3 page-sm:tw-gap-4 page-md:tw-grid-cols-2 page-lg:tw-col-start-1 page-lg:tw-row-start-2 page-lg:tw-grid-cols-1">
                  <AiInsightCard insight={data.aiInsight} />
                  <AiReportCard />
                </div>
              )}
            </div>

            <RecommendedRail items={data.recommendations} />
          </>
        )}
      </div>
    </div>
  );
};

export default StudentHomePage;
